import React from 'react';
import {useNavigate} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import BugReportIcon from '@mui/icons-material/BugReport';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';

import { useLoggedInUser } from 'src/services/api';
import { Auth } from 'src/services/api';
import { ThemeProvider } from 'src/theme';
import { LANDLORD_HOME_PATH } from 'src/routes';
import Responsive from 'src/components/responsive/Responsive';
import { Flex, FlexColumn } from 'src/components/flex';
import Link from 'src/components/link/Link';
import { useAppState } from 'src/AppStateProvider';
import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import { LogOutIcon, PanelLeftIcon } from 'src/icons';
import { links } from 'src/components/layout/landlord/links';
import { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import {ProfileImageRounded} from 'src/components/ProfileImageRounded';
import {DesktopNavigationInboxesLink} from 'src/components/layout/landlord/DesktopNavigationInboxesLink';

import navbarStyles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';
import layoutStyles from 'src/components/layout/landlord/LandlordLayout.module.css';
import 'src/components/layout/landlord/LandlordLayout.css';

type Props = {
    onNavigationChange: () => void;
}

export default function DesktopNavigationSidebar({
    onNavigationChange
}: Props) {
    const { setHelpSidebarOpened } = useAppState();
    const { isSidebarCollapsed, setSidebarCollapsed, sidebarWidth } = useLandlordLayoutContext();
    const navigate = useNavigate();
    const { data: loggedInUser } = useLoggedInUser();

    const handleLogout = () => {
        Auth.logout();
    };

    const handleClickSidebar = () => {
        setSidebarCollapsed(!isSidebarCollapsed);
    };

    const handleClickReportABug = async () => {
        const feedback = Sentry.getFeedback();
        const form = await feedback?.createForm();
        form?.appendToDom();
        form?.open();
    };

    const handleGoToSettings = () => {
        navigate('/landlord/inner/settings');
        onNavigationChange();
    };

    return (
        <>
            <Responsive desktop>
                <Box
                    className={clsx(
                        layoutStyles.sidebar,
                        layoutStyles.sidebarPadding,
                    )}
                    sx={{
                        width: sidebarWidth,
                        minWidth: sidebarWidth,
                    }}
                />
            </Responsive>

            <ThemeProvider>
                <Responsive desktop>
                    <Paper
                        square
                        className={layoutStyles.sidebar}
                        sx={{ width: sidebarWidth, minWidth: sidebarWidth }}
                    >
                        <FlexColumn height="100vh" justifyContent="space-between" className={navbarStyles.navbarFlexContainer}>
                            {/* this is the navigation bar header */}
                            <Flex flexDirection={isSidebarCollapsed ? 'column' : 'row'} justifyContent="space-between" pr={0.5}>
                                <Link href={LANDLORD_HOME_PATH} data-testid="home-link">
                                    <Flex height={60} p={1} alignItems="center">
                                        <img
                                            src="/img/reffie-logos/brain-transparent-256.png"
                                            alt="reffie-logo"
                                            height="40px"
                                        />
                                        {!isSidebarCollapsed && (
                                            <Typography color="primary" fontWeight="bold">
                                                Reffie
                                            </Typography>
                                        )}
                                    </Flex>
                                </Link>

                                <IconButton
                                    onClick={handleClickSidebar}
                                    data-testid="collapse-toggle"
                                    sx={{
                                        width: isSidebarCollapsed ? 60 : 'auto',
                                    }}
                                >
                                    <PanelLeftIcon />
                                </IconButton>
                            </Flex>

                            {/* these are the main nav links */}
                            <FlexColumn className="prettyScrollbar" flex={1} sx={{ overflowY: 'auto' }}>
                                <List>
                                    <DesktopNavigationInboxesLink onNavigationChange={onNavigationChange} isSidebarCollapsed={isSidebarCollapsed} />
                                    {links.map((link) => (
                                        <NavigationLink
                                            key={link.text}
                                            text={isSidebarCollapsed ? '' : link.text}
                                            icon={link.icon}
                                            link={link.link}
                                            onClick={onNavigationChange}
                                        />
                                    ))}
                                </List>
                            </FlexColumn>

                            {loggedInUser && (
                                <Flex px={1} py={0} alignItems="center" gap={1} flexDirection={isSidebarCollapsed ? 'column' : 'row'} sx={{
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }} onClick={handleGoToSettings} position="relative"
                                >
                                    <ProfileImageRounded url={loggedInUser.profile_photo_url} userName={loggedInUser.name} width={32} height={32} />
                                    {!isSidebarCollapsed && (
                                        <>
                                            <FlexColumn alignItems="start" px={1} py={2} gap={1} textAlign="start"
                                                justifyContent="start"
                                                overflow="hidden"
                                            >
                                                <Tooltip title={loggedInUser.name}>
                                                    <Typography fontWeight="600" fontSize={14} lineHeight="120%"
                                                        color="rgba(66, 64, 58, 1)}" className="ellipsisText"
                                                    >{loggedInUser.name}</Typography>
                                                </Tooltip>
                                                <Tooltip title={loggedInUser.email}>
                                                    <Typography variant="body2" fontWeight={400} width="100%"
                                                        color="rgba(158, 157, 152, 1)" className="ellipsisText"
                                                    >{loggedInUser.email}</Typography>
                                                </Tooltip>
                                            </FlexColumn>
                                            <ChevronRightIcon color="secondary" sx={{position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 0}} />
                                        </>
                                    )}
                                </Flex>)}

                            <Flex justifyContent="space-around" gap={0} px={1} pb={2} mobileFlexDirection="column" flexDirection={isSidebarCollapsed ? 'column' : 'row'} alignItems="center">
                                <Tooltip title="Report a bug" placement="top">
                                    <IconButton onClick={handleClickReportABug} ><BugReportIcon /></IconButton>
                                </Tooltip>
                                <Tooltip title="Help" placement="top">
                                    <IconButton onClick={() => setHelpSidebarOpened(true)} > <HelpIcon /></IconButton>
                                </Tooltip>
                                <Tooltip title="Logout" placement="top">
                                    <IconButton onClick={handleLogout} ><LogOutIcon /></IconButton>
                                </Tooltip>
                            </Flex>
                        </FlexColumn>
                    </Paper>
                </Responsive>
            </ThemeProvider>
        </>
    );
}
